import type { Router } from 'vue-router';

import { useAuthStore } from '@/stores/auth';

/**
 * Global auth gate. The whole app is behind login: any route without
 * `meta.public` requires a live session, and an already-authenticated user is
 * bounced off `/login`. The session probe (`ensureLoaded`) runs once on the
 * first navigation and is cached thereafter.
 */
export function registerAuthGuard(router: Router): void {
  router.beforeEach(async (to) => {
    const auth = useAuthStore();
    await auth.ensureLoaded();

    const isPublic = to.meta.public === true;

    if (!auth.isAuthenticated && !isPublic) {
      return {
        name: 'Login',
        query: to.fullPath && to.fullPath !== '/' ? { redirect: to.fullPath } : {},
      };
    }

    if (auth.isAuthenticated && to.name === 'Login') {
      const target = to.query.redirect;
      if (typeof target === 'string' && target.startsWith('/') && !target.startsWith('//')) {
        return target;
      }
      return { name: 'Collect' };
    }

    // Superadmin-only areas (user management). Non-superadmins are bounced to
    // the app home rather than shown a page they can't act on; the API enforces
    // the same boundary server-side regardless.
    if (to.meta.superadmin === true && !auth.isSuperadmin) {
      return { name: 'Collect' };
    }

    return true;
  });
}
